import { useState } from 'react';
import { Segmented, Switch, Tooltip } from 'antd';
import { FilterPanel } from './FilterPanel';
import { OptionList, type FilterOption, type FilterOptionGroup } from './OptionList';
import styles from './MultiFilterPanel.module.css';

/** Как сочетаются выбранные значения: любое из них или все сразу. */
export type MatchMode = 'any' | 'all';

// Режим комбинирования выбранных значений (И/ИЛИ).
export interface ModeToggleConfig {
  value: MatchMode;
  onChange: (v: MatchMode) => void;
  // Тултип должен описывать оба режима: подписи «И»/«ИЛИ» сами по себе не
  // говорят, что именно пересекается.
  tooltip?: string;
  // Подсказка, когда режим недоступен (выбрано меньше двух значений).
  disabledHint?: string;
}

// Переключатель «области» фильтра (для тегов).
export interface ScopeToggleConfig {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tooltip?: string;
  // Подсказка, когда переключатель недоступен (нет выбранных значений).
  disabledHint?: string;
}

interface MultiFilterPanelProps {
  title: string;
  value: string[];
  onChange: (v: string[]) => void;
  options?: FilterOption[];
  groups?: FilterOptionGroup[];
  modeToggle?: ModeToggleConfig;
  scopeToggle?: ScopeToggleConfig;
  // Закрыть выпадашку — после «Применить» или «Сбросить».
  onDone: () => void;
}

const MODE_OPTIONS: { value: MatchMode; label: string }[] = [
  { value: 'any', label: 'ИЛИ' },
  { value: 'all', label: 'И' },
];

// Совпадают ли множества (без учёта порядка) — чтобы не применять «пустой» апплай.
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((v) => set.has(v));
}

export function MultiFilterPanel({
  title,
  value,
  onChange,
  options,
  groups,
  modeToggle,
  scopeToggle,
  onDone,
}: MultiFilterPanelProps) {
  const [draft, setDraft] = useState<string[]>(value);
  const [draftMode, setDraftMode] = useState<MatchMode>(modeToggle?.value ?? 'any');
  const [draftScope, setDraftScope] = useState(scopeToggle?.checked ?? false);

  // Режим осмыслен только когда есть что комбинировать: на одном значении
  // «все сразу» и «любое из» — одно и то же.
  const modeDisabled = draft.length < 2;
  const scopeDisabled = draft.length === 0;

  const commit = (next: string[], mode: MatchMode, scope: boolean) => {
    if (!sameSet(next, value)) onChange(next);
    if (modeToggle && mode !== modeToggle.value) modeToggle.onChange(mode);
    if (scopeToggle && scope !== scopeToggle.checked) scopeToggle.onChange(scope);
    onDone();
  };

  return (
    <FilterPanel
      title={title}
      wide
      onApply={() => commit(draft, modeDisabled ? 'any' : draftMode, draftScope && !scopeDisabled)}
      onReset={() => commit([], 'any', false)}
    >
      <OptionList
        options={options}
        groups={groups}
        value={draft}
        onChange={setDraft}
        searchLabel={`Поиск: ${title}`}
      />

      {(modeToggle || scopeToggle) && (
        <div className={styles.modifiers}>
          {modeToggle && (
            <Tooltip title={modeDisabled ? modeToggle.disabledHint : modeToggle.tooltip}>
              <div className={styles.modifierRow}>
                <span className={styles.modifierLabel}>Совпадение</span>
                <Segmented<MatchMode>
                  size="small"
                  className={styles.modeControl}
                  value={modeDisabled ? 'any' : draftMode}
                  disabled={modeDisabled}
                  onChange={setDraftMode}
                  options={MODE_OPTIONS}
                />
              </div>
            </Tooltip>
          )}
          {scopeToggle && (
            <Tooltip title={scopeDisabled ? scopeToggle.disabledHint : scopeToggle.tooltip}>
              <div className={styles.modifierRow}>
                <Switch
                  size="small"
                  checked={draftScope && !scopeDisabled}
                  disabled={scopeDisabled}
                  onChange={setDraftScope}
                  aria-label={scopeToggle.label}
                />
                <span className={styles.modifierLabel}>{scopeToggle.label}</span>
              </div>
            </Tooltip>
          )}
        </div>
      )}
    </FilterPanel>
  );
}
